import { supabase } from '../config/supabase';
import { addCollaboratorToGarage } from './permissionService';

const DEV = import.meta.env.DEV;

/* ─── Create Invitation ─── */

/**
 * Create a pending garage invitation.
 *
 * @param {string} garageId
 * @param {string} email - Email of the user to invite
 * @param {string} inviterUserId - Supabase user ID of the inviter (must be owner)
 * @param {string} permissionLevel - Permission level to grant
 * @param {string} [message] - Optional personal message
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function createInvitation(garageId, email, inviterUserId, permissionLevel, message = null) {
  if (!garageId || !email || !inviterUserId) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  const normalizedEmail = email.trim().toLowerCase();

  try {
    // Resolve email → user UUID via RPC (bypasses RLS on users table)
    const { data: resolvedUserId, error: rpcErr } = await supabase
      .rpc('resolve_user_by_email', { lookup_email: normalizedEmail });

    if (rpcErr) {
      if (DEV) console.error('[Invitation] RPC resolve_user_by_email failed:', rpcErr.message);
      return { error: 'Could not verify user. Please try again.', code: 'LOOKUP_ERROR' };
    }

    // Invitee must be a registered user (user_id is NOT NULL in DB)
    if (!resolvedUserId) {
      return { error: 'This email is not registered on Proper Miles yet. Ask them to sign up first.', code: 'USER_NOT_FOUND' };
    }

    // Check for existing pending invitation
    const { data: existing } = await supabase
      .from('pending_invitations')
      .select('id')
      .eq('garage_id', garageId)
      .eq('invitee_email', normalizedEmail)
      .eq('status', 'pending')
      .maybeSingle();

    if (existing) {
      return { error: 'Invitation already pending for this user', code: 'DUPLICATE' };
    }

    // Insert the invitation
    const { data, error } = await supabase
      .from('pending_invitations')
      .insert({
        garage_id: garageId,
        user_id: resolvedUserId,
        invitee_email: normalizedEmail,
        invited_by: inviterUserId,
        permission_level: permissionLevel,
        message: message || null,
        status: 'pending',
      })
      .select()
      .single();

    if (error) {
      console.error('[Invitation] Create failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Invitation] Created:', data?.id, 'for', normalizedEmail);
    return { data, error: null };
  } catch (err) {
    console.error('[Invitation] Create error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/* ─── Get Pending Invitations ─── */

/**
 * Get all pending invitations for a user (by ID or email).
 *
 * @param {string} userId - Supabase auth user ID
 * @param {string} userEmail - User's email address
 * @returns {Promise<Array>}
 */
export async function getPendingInvitations(userId, userEmail) {
  if (!userId && !userEmail) return [];

  try {
    const normalizedEmail = userEmail?.trim().toLowerCase() || '';

    // Build OR filter for user_id match or email match
    let query = supabase
      .from('pending_invitations')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (userId && normalizedEmail) {
      query = query.or(`user_id.eq.${userId},invitee_email.eq.${normalizedEmail}`);
    } else if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.eq('invitee_email', normalizedEmail);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[Invitation] Fetch failed:', error.message);
      return [];
    }

    if (!data || data.length === 0) return [];

    // Fetch garage info via RPC (bypasses RLS)
    const garageIds = [...new Set(data.map((inv) => inv.garage_id).filter(Boolean))];
    let garageMap = {};

    for (const gid of garageIds) {
      const { data: garageRow } = await supabase.rpc('get_garage_info', { garage_uuid: gid });
      if (garageRow && garageRow.length > 0) {
        garageMap[gid] = garageRow[0];
      }
    }

    // Fetch inviter names via RPC (bypasses RLS)
    const inviterIds = [...new Set(data.map((inv) => inv.invited_by).filter(Boolean))];
    let inviterMap = {};

    if (inviterIds.length > 0) {
      const { data: inviters } = await supabase.rpc('get_user_display_names', { user_ids: inviterIds });

      if (inviters) {
        for (const u of inviters) {
          const name = u.first_name && u.last_name
            ? `${u.first_name} ${u.last_name}`
            : u.email || 'Unknown';
          inviterMap[u.id] = name;
        }
      }
    }

    // Merge garage info and inviter names into results
    const enriched = data.map((inv) => ({
      ...inv,
      garages: garageMap[inv.garage_id] || null,
      inviterName: inviterMap[inv.invited_by] || 'Unknown',
    }));

    if (DEV) console.log('[Invitation] Fetched', enriched.length, 'pending invitations');
    return enriched;
  } catch (err) {
    console.error('[Invitation] Fetch error:', err.message);
    return [];
  }
}

/* ─── Get Pending Invitation Count ─── */

/**
 * Get count of pending invitations for a user.
 *
 * @param {string} userId
 * @param {string} userEmail
 * @returns {Promise<number>}
 */
export async function getPendingInvitationCount(userId, userEmail) {
  if (!userId && !userEmail) return 0;

  try {
    const normalizedEmail = userEmail?.trim().toLowerCase() || '';

    let query = supabase
      .from('pending_invitations')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending');

    if (userId && normalizedEmail) {
      query = query.or(`user_id.eq.${userId},invitee_email.eq.${normalizedEmail}`);
    } else if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.eq('invitee_email', normalizedEmail);
    }

    const { count, error } = await query;

    if (error) {
      console.error('[Invitation] Count failed:', error.message);
      return 0;
    }

    return count || 0;
  } catch (err) {
    console.error('[Invitation] Count error:', err.message);
    return 0;
  }
}

/* ─── Accept Invitation ─── */

/**
 * Accept a pending invitation — adds user to garage collaborators.
 *
 * @param {string} invitationId
 * @param {string} userId - Accepting user's ID
 * @param {string} userEmail - Accepting user's email
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function acceptInvitation(invitationId, userId, userEmail) {
  if (!invitationId || !userId) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  try {
    // Fetch the invitation (no garages join — RLS would block it)
    const { data: invitation, error: fetchErr } = await supabase
      .from('pending_invitations')
      .select('*')
      .eq('id', invitationId)
      .single();

    if (fetchErr || !invitation) {
      return { error: 'Invitation not found', code: 'NOT_FOUND' };
    }

    if (invitation.status !== 'pending') {
      return { error: 'Invitation already processed', code: 'ALREADY_PROCESSED' };
    }

    // Fetch garage info via RPC (bypasses RLS)
    let garageInfo = null;
    const { data: garageRows } = await supabase.rpc('get_garage_info', { garage_uuid: invitation.garage_id });
    if (garageRows && garageRows.length > 0) {
      garageInfo = garageRows[0];
    }

    // Add user to garage via permission service
    const addResult = await addCollaboratorToGarage(
      invitation.garage_id,
      userId,
      userEmail,
      invitation.permission_level,
      invitation.invited_by
    );

    if (addResult.error) {
      // If already a collaborator, still delete the invitation
      if (addResult.code !== 'DUPLICATE') {
        return addResult;
      }
    }

    // Delete the invitation row
    await supabase
      .from('pending_invitations')
      .delete()
      .eq('id', invitationId);

    if (DEV) console.log('[Invitation] Accepted:', invitationId);

    return {
      data: {
        garage: garageInfo,
        permission: invitation.permission_level,
      },
      error: null,
    };
  } catch (err) {
    console.error('[Invitation] Accept error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/* ─── Decline Invitation ─── */

/**
 * Decline a pending invitation — removes the invitation row.
 *
 * @param {string} invitationId
 * @returns {Promise<{error?: string, code?: string}>}
 */
export async function declineInvitation(invitationId) {
  if (!invitationId) {
    return { error: 'Missing invitation ID', code: 'MISSING_PARAMS' };
  }

  try {
    const { error } = await supabase
      .from('pending_invitations')
      .delete()
      .eq('id', invitationId);

    if (error) {
      console.error('[Invitation] Decline failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Invitation] Declined:', invitationId);
    return { error: null };
  } catch (err) {
    console.error('[Invitation] Decline error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}
